import React from 'react';
import { Lightbulb } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

interface TimeAllocation {
  year: string;
  ourIp: number;
  ipOpportunity: number;
  other: number;
  ourIpProjects?: string[];
  ipOpportunityProjects?: string[];
  otherProjects?: string[];
}

interface ProjectTypePieChartProps {
  data: TimeAllocation[];
  selectedYear: string;
  onYearChange: (year: string) => void;
}

export function ProjectTypePieChart({ data, selectedYear, onYearChange }: ProjectTypePieChartProps) {
  const selectedData = data.find(d => d.year === selectedYear);
  
  const pieData = selectedData ? [
    { 
      name: 'Our IP', 
      value: selectedData.ourIp, 
      color: '#8B5CF6',
      projects: selectedData.ourIpProjects || []
    },
    { 
      name: 'IP Opportunity', 
      value: selectedData.ipOpportunity, 
      color: '#F59E0B',
      projects: selectedData.ipOpportunityProjects || []
    },
    { 
      name: 'Client Projects', 
      value: selectedData.other, 
      color: '#D1D5DB',
      projects: selectedData.otherProjects || []
    }
  ] : [];

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-100">
          <p className="font-medium text-gray-900 mb-1">
            {data.name} ({Math.round(data.value * 100)}%)
          </p>
          {data.projects.length > 0 ? (
            <ul className="text-sm text-gray-600 space-y-1">
              {data.projects.map((project: string, index: number) => (
                <li key={index}>• {project}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">No projects</p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Lightbulb className="w-5 h-5 text-indigo-600 mr-2" />
          <h2 className="text-xl font-semibold text-gray-800">Project Type Distribution</h2>
        </div>
        <select
          value={selectedYear}
          onChange={(e) => onYearChange(e.target.value)}
          className="ml-4 px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {data.map(item => (
            <option key={item.year} value={item.year}>{item.year}</option>
          ))}
        </select>
      </div>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={120}
              labelLine={false}
              label={({
                cx,
                cy,
                midAngle,
                innerRadius,
                outerRadius,
                value,
                name
              }) => {
                const RADIAN = Math.PI / 180;
                const radius = outerRadius * 1.1;
                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                const y = cy + radius * Math.sin(-midAngle * RADIAN);

                return (
                  <text
                    x={x}
                    y={y}
                    fill="#374151"
                    textAnchor={x > cx ? 'start' : 'end'}
                    dominantBaseline="central"
                    className="text-sm"
                  >
                    {`${name} (${Math.round(value * 100)}%)`}
                  </text>
                );
              }}
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}